import { type UIFacet } from 'src/components/types/UIFacet';
import { type ReduxApplicationState } from 'src/redux/combinedReducer';
import { type Facet } from 'src/redux/productsQuery/types/Facet';
import { buildListingsURL } from './builders/buildListingsURL';
import {
  type StoreFacet,
  buildFacetsIndex,
  deriveNextFacetsForOptionToggle,
} from './facets/deriveNextFacetsForOptionToggle';
import {
  buildListingsURLFromState,
  deriveListingsURLParts,
} from './state/buildListingsURLFromState';

type StoreFacetOption = StoreFacet['options'][number];

export type FacetOptionWithResetHref = Omit<StoreFacetOption, 'href'> & {
  href?: string;
};

export type FacetWithResetHrefs = Omit<Facet, 'options'> & {
  options: FacetOptionWithResetHref[];
};

const buildURLFromNextFacets = (state: ReduxApplicationState, nextFacets: StoreFacet[]): string => {
  return buildListingsURLFromState(state, {
    facets: nextFacets,
    page: 1,
  });
};

const deriveFacetOptionHref = (
  state: ReduxApplicationState,
  facetAlias: string,
  optionValue: string,
  facetType: 'single' | 'multi',
  facetsIndex: ReturnType<typeof buildFacetsIndex>,
): string | undefined => {
  const nextFacets = deriveNextFacetsForOptionToggle(
    state.productsQuery.facets,
    facetAlias,
    optionValue,
    facetsIndex,
    facetType,
  );

  if (!nextFacets) {
    return undefined;
  }

  return buildURLFromNextFacets(state, nextFacets);
};

const deriveFacetOptionResetHref = (
  state: ReduxApplicationState,
  facetAlias: string,
  optionValue: string,
): string | undefined => {
  const facetsIndex = buildFacetsIndex(state.productsQuery.facets);
  const nextFacets = deriveNextFacetsForOptionToggle(
    state.productsQuery.facets,
    facetAlias,
    optionValue,
    facetsIndex,
  );

  if (!nextFacets) {
    return undefined;
  }

  return buildURLFromNextFacets(state, nextFacets);
};

export const adaptStoreToActiveFacetResetHrefs = (
  state: ReduxApplicationState,
  activeFacets: Facet[],
): FacetWithResetHrefs[] => {
  return activeFacets.map((facet) => ({
    ...facet,
    options: facet.options.map((option) => ({
      ...option,
      href: option.active
        ? deriveFacetOptionResetHref(state, facet.alias, option.value)
        : undefined,
    })),
  }));
};

export const getClearAllFacetsHref = (state: ReduxApplicationState): string => {
  const { basePath, hash, queryParams } = deriveListingsURLParts(state, { page: 1 });
  return buildListingsURL(basePath, '', queryParams, hash);
};

export const adaptStoreToUIFacetHrefs = (
  state: ReduxApplicationState,
  uiFacets: UIFacet[],
): UIFacet[] => {
  if (uiFacets.length === 0) {
    return uiFacets;
  }

  const facetsIndex = buildFacetsIndex(state.productsQuery.facets);

  return uiFacets.map((facet) => ({
    ...facet,
    options: facet.options.map((option) => {
      const href = deriveFacetOptionHref(state, facet.alias, option.value, facet.type, facetsIndex);
      return { ...option, href };
    }),
  }));
};
